import { useState, type FormEvent } from "react";
import { Button, FormRow, FormSection, HelpList, Input, Switch } from "@omnabase/ui";
import type { Me } from "@omnabase/shared";
import { adminApi, isReauth, useAdminRead } from "../../lib/admin";
import { toast } from "../../shell/toast";
import { ReauthDialog } from "./ReauthDialog";

/** M11 round two (brief choice 10 c, d): the platform's own switches on the admin page, outside any workspace: the sign-up switch (the
 *  global `workspace_signup` row; off stops both ways of creating a workspace, never an invitation) and "Lift SES's block" for one address
 *  (SES's account-level suppression list drops every later mail to a bounced address, sign-in codes included). Both are audited and need
 *  a fresh sign-in: a `reauth` answer opens the code dialog and the action is sent again. */
const SIGNUP_RULES = ["Off: the creation form and the sign-in library both refuse", "Invitations still work", "Existing workspaces are untouched"];
const SES_RULES = ["Use it after a bounce made SES drop a person's sign-in codes", "Needs the IAM step ses:DeleteSuppressedDestination (docs/briefs/m11-round-two-iam.md)"];

export function PlatformTools({ me }: { me: Me }) {
  const signup = useAdminRead(adminApi.signup, "signup");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState<{ act: () => Promise<void>; what: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (act: () => Promise<void>, what: string) => {
    setBusy(true);
    try { await act(); } catch (e) { if (isReauth(e)) setPending({ act, what }); else toast((e as Error).message, { kind: "error" }); } finally { setBusy(false); }
  };
  const flip = (on: boolean) => run(async () => { await adminApi.setSignup(on); toast(on ? "Sign-ups are open" : "Sign-ups are closed · invitations still work"); await signup.reload(); }, "The sign-up switch");
  const lift = (e: FormEvent) => { e.preventDefault(); const typed = email.trim(); if (!typed) return; void run(async () => { const r = await adminApi.unsuppress(typed); toast(`SES's block lifted for ${r.email}`, { kind: "success" }); setEmail(""); }, "Lifting SES's block"); };
  return (
    <>
      <FormSection title="Platform" intro="Switches for the whole platform, not one workspace. Each change is audited.">
        <FormRow label="Sign-ups" help={<>Whether strangers may create workspaces (the switch workspace_signup).<HelpList items={SIGNUP_RULES} /></>}>
          <Switch checked={signup.data?.on ?? false} disabled={busy || !signup.data} onChange={(on) => void flip(on)} label="New workspaces may be created" />
        </FormRow>
        <FormRow label="Lift SES's block" help={<>Takes one address off Amazon SES's account-wide suppression list.<HelpList items={SES_RULES} /></>}>
          <form onSubmit={lift} style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Input className="ob-input--text" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" aria-label="Address to lift" />
            <Button type="submit" disabled={busy || !email.trim()}>Lift SES's block</Button>
          </form>
        </FormRow>
      </FormSection>
      {pending && <ReauthDialog email={me.user.email} what={pending.what} onClose={() => setPending(null)} onConfirmed={() => { const p = pending; setPending(null); void run(p.act, p.what); }} />}
    </>
  );
}
